<script lang="ts">
  import type { ManageAPI } from "~/manage-api/types";
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import { currentOfferId, currentOfferDetails, currentRequestStoreKey, closeOfferModal } from "../../stores/offerModal.ts";
  import { formatDateRange } from "../common/date-utils";
  import { onMount } from 'svelte';
  
  const browser = typeof window !== 'undefined';
  
  export let request_store_key: StoresKey;
  export let displayTitle:boolean;
  // Badge styling props
  export let badgeBackgroundColor: string = "";
  export let badgeTextColor: string = "";
  export let badgeBorderRadius: string = "";
  export let badgeFontSize: string = "";
  
  // Modal styling props
  export let modalBorderRadius: string = "";
  export let modalBackgroundColor: string = "";
  export let modalTextColor: string = "";
  export let modalPaddingVertical: string = "";
  export let modalPaddingHorizontal: string = "";
  export let modalMaxWidth: string = "";
  export let modalCloseButtonBorderRadius: string = "";
  
  // Use the global store to track offer ID and details
  $: modalId = $currentOfferId;
  $: offer = $currentOfferDetails;
  $: actualRequestStoreKey = $currentRequestStoreKey || request_store_key;
  
  // Badge styling - use custom props if provided
  $: badgeStyle = "";
  $: {
    const styleProps = [];
    if (badgeBackgroundColor) styleProps.push(`--offer-modal-badge-bg-color: ${badgeBackgroundColor}`);
    if (badgeTextColor) styleProps.push(`--offer-modal-badge-text-color: ${badgeTextColor}`);
    if (badgeBorderRadius) styleProps.push(`--offer-modal-badge-border-radius: ${badgeBorderRadius}`);
    if (badgeFontSize) styleProps.push(`--offer-modal-badge-font-size: ${badgeFontSize}`);
    
    badgeStyle = styleProps.join('; ');
  }
  
  // Modal styling - use custom props if provided
  $: modalStyle = "";
  $: {
    const styleProps = [];
    if (modalBorderRadius) styleProps.push(`--offer-modal-border-radius: ${modalBorderRadius}`);
    if (modalBackgroundColor) styleProps.push(`--offer-modal-background-color: ${modalBackgroundColor}`);
    if (modalTextColor) styleProps.push(`--offer-modal-text-color: ${modalTextColor}`);
    if (modalPaddingVertical) styleProps.push(`--offer-modal-padding-vertical: ${modalPaddingVertical}`);
    if (modalPaddingHorizontal) styleProps.push(`--offer-modal-padding-horizontal: ${modalPaddingHorizontal}`);
    if (modalMaxWidth) styleProps.push(`--offer-modal-max-width: ${modalMaxWidth}`);
    if (modalCloseButtonBorderRadius) styleProps.push(`--offer-modal-close-button-border-radius: ${modalCloseButtonBorderRadius}`);
    
    modalStyle = styleProps.join('; ');
  }
  
  // Modal handling
  let previouslyFocused: Element | null = null;
  let modalBackdrop: HTMLElement;

  // When modalId changes and the modal should open, save previously focused element
  $: if (modalId) {
    previouslyFocused = document.activeElement;
    setTimeout(() => {
      if (modalBackdrop) {
        modalBackdrop.focus();
      }
    }, 0);
  }

  function closeModal(e?: Event) {
    if (e) {
      e.preventDefault();
    }
    
    // Use the store function to properly close the modal
    closeOfferModal();
    
    if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeModal();
    }
  }
  
  // Store context and content variables
  let store: ManageAPI.Store | undefined;
  let store_href = "";
  let logo = "";
  let alt = "";
  
  // Update store info when offer or context changes
  $: if (offer && actualRequestStoreKey) {
    getStoreInfo();
  }
  
  function getStoreInfo() {
    if (!offer) return;
    
    const storesContext = getStoresContext(actualRequestStoreKey);
    const { reverse_router, stores, scdata, site } = storesContext;
    
    store = stores.value && stores.value.find((x: ManageAPI.Store) => x.id == offer.store?.id);
    store_href = store && reverse_router.value && scdata.value ? reverse_router.value.routeStore(store, scdata.value) : "";
    logo = store?.logoURL || "";
    alt = store?.name || site.value?.name || "";
  }

  // Cleanup function to ensure modal class is removed when component is destroyed
  onMount(() => {
    return () => {
      if (browser) {
        document.documentElement.classList.remove('demo2025-modal-open');
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    };
  });
</script>

{#if modalId && offer}
  <div 
    class="modal-backdrop"
    role="button"
    on:click={closeModal}
    on:keydown={handleKeydown}
    tabindex="0"
    bind:this={modalBackdrop}
  >
    <div 
      class="modal-wrapper" 
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="presentation"
      style={modalStyle}
    >
      <div class="modal-content offer-dialog" role="dialog" aria-modal="true">
        <div class="dialog-content" role="document">
          <!-- Mobile close button inside modal -->
          <button 
            type="button" 
            class="mobile-close-button" 
            on:click={closeModal}
            aria-label="Close dialog"
          >
            <span class="mobile-close-icon"></span>
          </button>
          
          <!-- Offer Content -->
          <div class="image-container">
            {#if offer.images && offer.images.length}
              <img src={offer.images.at(0)} {alt} />
            {:else}
              <img src={logo} {alt} />
            {/if}
          </div>
          <div class="offer-modal-details">
            <div class="offer-detail"> 
              <div class="offer-meta">
                <p class="date-range">
                  {formatDateRange(offer.validityStartDate, offer.endDate)}
                </p>
                {#if store}
                  <a class="publisher text-gray" href={store_href} style={badgeStyle}>{alt}</a>
                {:else}
                  <span class="publisher text-gray" style={badgeStyle}>{alt}</span>
                {/if}
              </div>
              {#if displayTitle}
                <div class="offer-title">{offer.title}</div>
              {/if}
              <h2 class="description">
                {offer.description1}
                {offer.description2}
              </h2>
              {#if offer.longDescription}
                <p class="longDescription">{offer.longDescription}</p>
              {/if}
            </div>
          </div>
        </div>
      </div>
      <!-- Desktop close button outside modal -->
      <button 
        type="button" 
        class="desktop-close-button" 
        on:click={closeModal}
        aria-label="Close dialog"
      >
        <span class="close-icon"></span>
      </button>
    </div>
  </div>
{/if}

<style lang="scss">
  /* Modal Styles */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
    padding-top: var(--header-height, 60px);
    overflow: hidden;
    
    @media screen and (max-width: 767px) {
      padding: 10px;
      align-items: center;
    }
    
    @media screen and (max-width: 475px) {
      padding: 5px;
    }
  }

  .modal-wrapper {
    position: relative;
    width: 100%;
    height: fit-content;
    max-width: var(--offer-modal-max-width, 500px);
    margin: auto;
    max-height: 85vh;
    
    @media screen and (max-width: 767px) {
      max-height: calc(100vh - 20px);
    }
    
    @media screen and (max-width: 475px) {
      max-height: calc(100vh - 10px);
    }
  }

  .modal-content {
    background: var(--offer-modal-background-color, white);
    border-radius: var(--offer-modal-border-radius, 24px);
    padding: 0;
    width: 100%;
    height: 100%;
    color: var(--offer-modal-text-color, #000);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    
    &:focus {
      outline: none;
    }
  }

  .dialog-content {
    padding: var(--offer-modal-padding-vertical, 40px) var(--offer-modal-padding-horizontal, 40px);
    padding-top: calc(var(--offer-modal-padding-vertical, 40px) + 10px);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* Desktop close button styles */
  .desktop-close-button {
    position: absolute;
    top: -50px;
    right: -50px;
    background: var(--offer-modal-background-color, white);
    border: none;
    border-radius: var(--offer-modal-close-button-border-radius, 8px);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1001;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);

    &:hover {
      background: #f5f5f5;
      
      .close-icon::before,
      .close-icon::after {
        background-color: var(--offer-modal-text-color, #000);
      }
    }

    /* Hide on mobile */
    @media screen and (max-width: 767px) {
      display: none;
    }
  }
  
  /* Mobile close button styles */
  .mobile-close-button {
    position: absolute;
    top: 5px;
    right: 5px;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: none;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 1002;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
    transition: background-color 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 1);
    }

    /* Show only on mobile */
    @media screen and (max-width: 767px) {
      display: flex;
    }
  }
  
  .close-icon {
    position: relative;
    width: 16px;
    height: 16px;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--offer-modal-text-color, #000);
      transition: background-color 0.2s;
    }
    
    &::before {
      transform: rotate(45deg);
    }
    
    &::after {
      transform: rotate(-45deg);
    }
  }
  
  .mobile-close-icon {
    position: relative;
    width: 15px;
    height: 15px;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 1.5px;
      background-color: rgba(0, 0, 0, 1);
      transition: background-color 0.2s;
    }
    
    &::before {
      transform: rotate(45deg);
    }
    
    &::after {
      transform: rotate(-45deg);
    }
  }

  /* Offer Content Styles */
  .offer-meta {
    font-family: var(--liferay-font-family-base);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: var(--offer-modal-text-color, #000);
    margin-bottom: 1rem;
    
    @media screen and (max-width: 475px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
  }
  
  .date-range {
    font-size: 14px;
    margin: 0;
  }

  .description {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 2%;
    color: var(--offer-modal-text-color, #000);
    line-height: 1.4;
    
    @media screen and (max-width: 475px) {
      font-size: 14px;
    }
  }
  
  .longDescription {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--offer-modal-text-color, #000);
    margin: 0;
    
    @media screen and (max-width: 475px) {
      font-size: 14px;
    }
  }

  .offer-modal-details {
    width: 100%;
    flex: 1;
  }

  .publisher {
    font-size: var(--offer-modal-badge-font-size, 16px);
    font-weight: 600;
    text-decoration: none;
    text-transform: uppercase;
    background-color: var(--offer-modal-badge-bg-color, #f5f5f5);
    color: var(--offer-modal-badge-text-color, inherit);
    padding: 5px 10px;
    border-radius: var(--offer-modal-badge-border-radius, 4px);
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
    white-space: nowrap;
    
    @media screen and (max-width: 475px) {
      font-size: calc(var(--offer-modal-badge-font-size, 16px) - 2px);
    }
  }

  a.publisher:hover {
    color: var(--offer-modal-badge-text-color, inherit);
    text-decoration: none;
  }

  .image-container {
    position: relative;
    background: var(--offer-modal-background-color, #fff);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    border-radius: var(--offer-modal-border-radius, 10px);
    height: 300px;
    
    @media screen and (max-width: 768px) {
      background-color: #eaeaea52;
      height: 250px;
    }
    
    @media screen and (max-width: 475px) {
      height: 200px;
    }
  }
  
  .image-container img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: var(--offer-modal-border-radius, 10px);
  }
</style> 